import { useEffect, useState } from "react";
import { Trans } from "@lingui/react/macro";
import { Carousel } from "@marketing-sites/ui/primitives";
import { cn } from "@marketing-sites/utils/cn";

import { InboxConversation, InboxFrame, InboxList } from "./inbox";
import { Messenger } from "./messenger";
import { Scaled } from "./primitives";
import { TICKETS, type DemoPlacement, type Industry } from "./tickets";

const HELPDESK_TAB_IDS: Record<Industry, string> = {
  ecommerce: "btn_solutions_ecommerce_demo_helpdesk_tab",
  technology: "btn_solutions_technology_demo_helpdesk_tab",
  gaming: "btn_solutions_gaming_demo_helpdesk_tab",
  fintech: "btn_solutions_fintech_demo_helpdesk_tab",
};
const HOME_HELPDESK_TAB_IDS: Record<Industry, string> = {
  ecommerce: "btn_home_ecommerce_demo_helpdesk_tab",
  technology: "btn_home_technology_demo_helpdesk_tab",
  gaming: "btn_home_gaming_demo_helpdesk_tab",
  fintech: "btn_home_fintech_demo_helpdesk_tab",
};
const FIN_TAB_IDS: Record<Industry, string> = {
  ecommerce: "btn_solutions_ecommerce_demo_fin_tab",
  technology: "btn_solutions_technology_demo_fin_tab",
  gaming: "btn_solutions_gaming_demo_fin_tab",
  fintech: "btn_solutions_fintech_demo_fin_tab",
};
const HOME_FIN_TAB_IDS: Record<Industry, string> = {
  ecommerce: "btn_home_ecommerce_demo_fin_tab",
  technology: "btn_home_technology_demo_fin_tab",
  gaming: "btn_home_gaming_demo_fin_tab",
  fintech: "btn_home_fintech_demo_fin_tab",
};

const TAB_BORDER = "border-[#d3cec6]";
const TAB_MESSENGER_WIDTH = 400;
const TAB_INBOX_WIDTH = 800;

function MobileTab({
  index,
  product,
  analyticsId,
}: {
  index: number;
  product: "helpdesk" | "fin";
  analyticsId: string;
}) {
  return (
    <Carousel.Thumbnail
      index={index}
      analyticsId={analyticsId}
      className={cn(
        "data-selected:bg-off-white relative flex h-[50px] flex-1 cursor-pointer items-center gap-1.5 bg-white px-5 font-mono text-[11px] uppercase text-[#111]",
        index === 0 && cn("border-r", TAB_BORDER),
      )}
      progress={({ ref, isPlaying, isSelected }) => (
        <span
          ref={ref}
          className={cn(
            "absolute left-0 top-0 h-0.5 w-full origin-left",
            product === "helpdesk" ? "bg-helpdesk" : "bg-fin",
          )}
          style={{
            animation: "carouselAutoplay linear",
            opacity: isPlaying && isSelected ? 1 : 0,
          }}
        />
      )}
    >
      <span
        aria-hidden
        className={cn(
          "block size-2 shrink-0",
          product === "helpdesk" ? "bg-helpdesk" : "bg-fin",
        )}
      />
      {product === "helpdesk" ? (
        <Trans>Intercom helpdesk</Trans>
      ) : (
        <Trans>Fin AI Agent</Trans>
      )}
    </Carousel.Thumbnail>
  );
}

// Positions match the Figma frame's 362×341 panel; only the front product
// changes between tabs.
function MobileTabProducts({
  industry,
  placement,
}: {
  industry: Industry;
  placement: DemoPlacement;
}) {
  const { emblaApi } = Carousel.useCarousel();
  const [selected, setSelected] = useState(0);
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect).on("reInit", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect).off("reInit", onSelect);
    };
  }, [emblaApi]);
  const messengerFront = selected === 1;
  const tickets = TICKETS[industry];
  const ticket = tickets[0];
  const inbox = (
    <div
      className={cn(
        "absolute left-[3.53%] top-[5.87%] aspect-[640/603] w-[87.05%] transition-opacity duration-300",
        messengerFront ? "z-0 opacity-50" : "z-10",
      )}
    >
      <Scaled width={TAB_INBOX_WIDTH}>
        <InboxFrame>
          <InboxList
            tickets={tickets}
            activeIndex={0}
            onSelect={() => {}}
            className="w-[220px] border-r border-black/[0.06] pb-0"
          />
          <InboxConversation
            ticket={ticket}
            industry={industry}
            placement={placement}
            className="flex"
          />
        </InboxFrame>
      </Scaled>
    </div>
  );
  const messenger = (
    <div
      className={cn(
        "absolute left-[56.02%] top-[12.3%] h-[74.3%] w-[40.63%] drop-shadow-[0_8px_20px_rgba(0,0,0,0.12)] transition-opacity duration-300",
        messengerFront ? "z-10" : "z-0 opacity-50",
      )}
    >
      <Scaled width={TAB_MESSENGER_WIDTH}>
        <Messenger ticket={ticket} industry={industry} placement={placement} />
      </Scaled>
    </div>
  );
  return (
    <div
      inert
      className="bg-chalk pointer-events-none absolute inset-0 overflow-hidden"
    >
      {inbox}
      {messenger}
    </div>
  );
}

export function MobileHeroTabs({
  industry,
  placement,
}: {
  industry: Industry;
  placement: DemoPlacement;
}) {
  return (
    <Carousel.Root
      autoplayOptions={{ delay: 6000 }}
      priority
      className={cn(
        "mx-auto max-w-[720px] overflow-hidden border bg-white",
        TAB_BORDER,
      )}
    >
      <Carousel.Thumbnails className={cn("flex gap-0 border-b", TAB_BORDER)}>
        <MobileTab
          index={0}
          product="helpdesk"
          analyticsId={
            placement === "home"
              ? HOME_HELPDESK_TAB_IDS[industry]
              : HELPDESK_TAB_IDS[industry]
          }
        />
        <MobileTab
          index={1}
          product="fin"
          analyticsId={
            placement === "home"
              ? HOME_FIN_TAB_IDS[industry]
              : FIN_TAB_IDS[industry]
          }
        />
      </Carousel.Thumbnails>
      {/* Both tabs show the same two products, so one shared pair sits over
          empty slides and only its stacking follows the selected tab. */}
      <div className="relative">
        <Carousel.Viewport>
          <Carousel.Container>
            <Carousel.Slide index={0} className="aspect-[362/341] w-full" />
            <Carousel.Slide index={1} className="aspect-[362/341] w-full" />
          </Carousel.Container>
        </Carousel.Viewport>
        <MobileTabProducts industry={industry} placement={placement} />
      </div>
    </Carousel.Root>
  );
}
